/*
  js confirm for CF7

  Usage:
    - jsで生成される確認画面
    - あくまで確認画面であり、送信値はformに入力した値が送信される
    - ボタン類は自動で付与されるので記載しなくても良い
    - confirmにて表示させたいラベルに対して.js-form-labelを付与しておく
    - 完了画面はなく、CF7が吐き出すcomplete文の位置へスクロール遷移する仕様
*/
const STATE = Object.freeze({
  CONFIRM: "is-confirm",
  COMPLETE: "is-complete",
});

const isLocal =
  location.hostname === "localhost" ||
  location.hostname === "127.0.0.1";

const ENTITY_MAP = Object.freeze({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
});

const normalizeName = (name = "") => name.replace(/\[\]$/, "");

const escapeHTML = (value = "") =>
  String(value).replace(/[&<>"']/g, (ch) => ENTITY_MAP[ch]);

const safeSmooth = (fnSmooth, fnFallback) => {
  try {
    fnSmooth();
  } catch {
    fnFallback();
  }
};

const scrollToTop = () =>
  safeSmooth(
    () => window.scrollTo({ top: 0, behavior: "smooth" }),
    () => window.scrollTo(0, 0),
  );

const scrollToElement = (el) => {
  if (!el) return scrollToTop();
  safeSmooth(
    () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
    () => el.scrollIntoView(),
  );
};

const scrollToResponse = (wrapper) =>
  scrollToElement(wrapper?.querySelector(".wpcf7-response-output"));

const isHiddenByAttr = (el) =>
  el.hidden || el.getAttribute("aria-hidden") === "true";

const isDisplayNone = (el) =>
  (el.style?.display || "").toLowerCase() === "none";

const isFieldHidden = (field) => {
  if (!field) return true;

  const type = (field.type || "").toLowerCase();
  if (type === "hidden") return true;
  if (isHiddenByAttr(field)) return true;

  for (let node = field; node && node.nodeType === Node.ELEMENT_NODE; ) {
    const el = /** @type {HTMLElement} */ (node);
    if (isHiddenByAttr(el) || isDisplayNone(el)) return true;
    node = el.parentElement;
  }

  return false;
};

const textNormalize = (s = "") => s.replace(/[\s\u3000]+/g, " ").trim();
const compact = (s = "") => s.replace(/[\s\u3000]+/g, "");

const collectFieldValue = (field) => {
  if (isFieldHidden(field)) return null;

  const type = (field.type || "").toLowerCase();
  const tag = field.tagName.toLowerCase();

  if (type === "submit" || type === "button") return null;

  if (type === "checkbox" || type === "radio") {
    if (!field.checked) return null;
    return (
      textNormalize(field.closest("label")?.textContent) || field.value || null
    );
  }

  if (tag === "select") {
    const selected = [...field.options].filter((o) => o.selected);
    return selected.length
      ? selected.map((o) => o.text || o.value || "").join("\n")
      : null;
  }

  if (type === "file") {
    return field.files?.length
      ? [...field.files].map((f) => f.name).join(", ")
      : null;
  }

  return field.value || null;
};

const hasFilledValue = (field) => {
  if (!field || field.disabled || isFieldHidden(field)) return false;

  const type = (field.type || "").toLowerCase();
  const tag = field.tagName.toLowerCase();

  if (type === "checkbox" || type === "radio") return field.checked;

  if (tag === "select") {
    const opts = [...field.options];
    return field.multiple
      ? opts.some((o) => o.selected && o.value !== "")
      : field.value !== "";
  }

  if (type === "file") return !!field.files?.length;

  return compact(field.value || "") !== "";
};

const validateRequiredFields = (form) => {
  const requiredControls = [
    ...form.querySelectorAll(".wpcf7-validates-as-required"),
  ];

  const invalid = requiredControls.find((control) => {
    const targets = control.matches("input, select, textarea")
      ? [control]
      : [...control.querySelectorAll("input, select, textarea")];

    return !targets.some(hasFilledValue);
  });

  if (!invalid) return true;

  window.alert("必須の内容を全て入力してください");
  invalid.focus?.();
  return false;
};

const collectTargetsFromContainers = (containers) => {
  const names = new Set();

  containers.forEach((container) => {
    container.querySelectorAll("input, select, textarea").forEach((field) => {
      const type = (field.type || "").toLowerCase();
      if (type === "submit" || type === "button" || isFieldHidden(field))
        return;

      const name = normalizeName(field.name || "");
      name && names.add(name);
    });
  });

  return [...names].join(" ");
};

const gatherContainersForLabel = (label) => {
  const containers = [];

  for (let node = label.nextSibling; node; node = node.nextSibling) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = /** @type {HTMLElement} */ (node);

    if (
      el.classList.contains("js-form-label") ||
      el.classList.contains("form-item__label")
    )
      break;
    if (el.classList.contains("form-item__form")) containers.push(el);
  }

  if (!containers.length) {
    const parent = label.parentElement;
    parent?.classList.contains("form-item__form") && containers.push(parent);
  }

  return containers;
};

const el = (tag, { className, text, html, attrs, dataset } = {}) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  if (html != null) node.innerHTML = html;
  if (attrs) Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
  if (dataset)
    Object.entries(dataset).forEach(([k, v]) => (node.dataset[k] = v));
  return node;
};

const buildConfirmList = (formBody, confirmContainer) => {
  confirmContainer.innerHTML = "";

  const list = el("div", { className: "cf7-confirm__list form-item" });

  formBody.querySelectorAll(".form-item").forEach((item) => {
    item.querySelectorAll(".js-form-label").forEach((label) => {
      const labelText = textNormalize(label.textContent);
      const labelHTML = (label.innerHTML || "").trim();
      if (!labelText && !labelHTML) return;

      const containers = gatherContainersForLabel(label);
      if (!containers.length) return;

      const targets = collectTargetsFromContainers(containers);
      if (!targets) return;

      const term = el("span", {
        className: "cf7-confirm__term form-item__label",
        ...(labelHTML ? { html: labelHTML } : { text: labelText }),
      });

      const desc = el("div", {
        className: "cf7-confirm__desc form-item__form js-cf7-confirm-value",
        dataset: { confirmTarget: targets },
      });

      list.append(term, desc);
    });
  });

  if (!list.children.length) return;

  confirmContainer.appendChild(list);

  const actions = el("div", { className: "cf7-confirm__actions" });
  actions.append(
    el("button", {
      className: "cf7-confirm__back wpcf7-submit --back js-cf7-confirm-back",
      text: "戻る",
      attrs: { type: "button" },
    }),
    el("button", {
      className:
        "cf7-confirm__submit wpcf7-submit --send js-cf7-confirm-submit",
      text: "送信する",
      attrs: { type: "button" },
    }),
  );

  confirmContainer.appendChild(actions);
};

const ensureStructure = (wrapper, form) => {
  const ensure = (selector, factory) =>
    wrapper.querySelector(selector) || factory();

  let formBody = wrapper.querySelector(".js-cf7-form-body");
  if (!formBody) {
    formBody = el("div", { className: "cf7-form js-cf7-form-body" });
    while (form.firstChild) formBody.appendChild(form.firstChild);
    form.appendChild(formBody);
  }

  const submitInput = formBody.querySelector(
    'input[type="submit"], button[type="submit"]',
  );
  if (submitInput) {
    submitInput.classList.add("js-cf7-submit");
    submitInput.hidden = true;
    submitInput.style.display = "none";
    submitInput.setAttribute("aria-hidden", "true");
  }

  const submitContainer = submitInput?.closest(".form-item__submit");

  let confirmOpen = wrapper.querySelector(".js-cf7-confirm-open");
  if (!confirmOpen && submitContainer && submitInput) {
    confirmOpen = el("button", {
      className: "cf7-confirm__open wpcf7-submit --confirm js-cf7-confirm-open",
      text: "確認へ",
      attrs: { type: "button" },
    });
  }
  if (confirmOpen && submitContainer && submitInput) {
    submitContainer.insertBefore(confirmOpen, submitInput);
  }

  const confirmContainer = ensure(".js-cf7-confirm", () => {
    const node = el("div", { className: "cf7-confirm js-cf7-confirm" });
    node.hidden = true;
    form.appendChild(node);
    return node;
  });

  buildConfirmList(formBody, confirmContainer);

  const completeContainer = ensure(".js-cf7-complete", () => {
    const node = el("div", { className: "cf7-complete js-cf7-complete" });
    node.hidden = true;

    const inner = el("div", { className: "cf7-complete__inner" });
    inner.append(
      el("p", {
        className: "cf7-complete__message",
        text: "送信が完了しました。",
      }),
      el("button", {
        className: "cf7-complete__close js-cf7-complete-close",
        text: "閉じる",
        attrs: { type: "button" },
      }),
    );

    node.appendChild(inner);
    form.appendChild(node);
    return node;
  });

  return {
    formBody,
    submitInput,
    confirmOpen,
    confirmContainer,
    confirmBack: confirmContainer.querySelector(".js-cf7-confirm-back"),
    confirmSubmit: confirmContainer.querySelector(".js-cf7-confirm-submit"),
    completeContainer,
    completeClose: completeContainer.querySelector(".js-cf7-complete-close"),
  };
};

const populateConfirm = (form, confirmContainer) => {
  confirmContainer.querySelectorAll(".js-cf7-confirm-value").forEach((node) => {
    const targets =
      node.dataset.confirmTarget?.split(/\s+/).filter(Boolean) ?? [];
    const collected = [];

    targets.forEach((target) => {
      const fields = form.querySelectorAll(
        `[name="${target}"], [name="${target}[]"]`,
      );
      if (!fields.length) return;

      const values = [...fields]
        .map(collectFieldValue)
        .filter((v) => v != null && v !== "");

      values.length && collected.push(values.join("\n"));
    });

    node.innerHTML = collected.length
      ? escapeHTML(collected.join("\n")).replace(/\n/g, "<br>")
      : "未入力";
  });
};

const setupForm = (form) => {
  const wrapper = form.closest(".wpcf7");
  if (!wrapper || !form.querySelector(".js-form-label")) return;

  const ui = ensureStructure(wrapper, form);
  const {
    formBody,
    submitInput,
    confirmOpen,
    confirmContainer,
    confirmBack,
    confirmSubmit,
    completeContainer,
    completeClose,
  } = ui;
  if (!confirmOpen || !confirmContainer || !formBody || !submitInput) return;

  const setMode = (mode) => {
    const isConfirm = mode === "confirm";
    const isComplete = mode === "complete";
    const isInput = !isConfirm && !isComplete;

    formBody.hidden = !isInput;
    confirmContainer.hidden = !isConfirm;
    completeContainer && (completeContainer.hidden = !isComplete);

    // 状態クラスは「必ずどれか1つ」になるように一度全消し
    wrapper.classList.remove(STATE.CONFIRM, STATE.COMPLETE);
    if (isConfirm) wrapper.classList.add(STATE.CONFIRM);
    if (isComplete) wrapper.classList.add(STATE.COMPLETE);

    if (isConfirm) return scrollToElement(wrapper);
    if (isComplete) return scrollToResponse(wrapper);
  };

  confirmOpen.addEventListener("click", (e) => {
    e.preventDefault();
    if (typeof form.reportValidity === "function" && !form.checkValidity())
      return form.reportValidity();
    if (!validateRequiredFields(form)) return;

    populateConfirm(form, confirmContainer);
    setMode("confirm");
  });

  confirmBack?.addEventListener(
    "click",
    (e) => (e.preventDefault(), setMode("input")),
  );
  confirmSubmit?.addEventListener("click", (e) => {
    e.preventDefault();
    if (confirmSubmit.disabled) return;
    confirmSubmit.disabled = true;
    submitInput.click();
  });

  wrapper.addEventListener("wpcf7submit", () => setMode("input"));

  ["wpcf7invalid", "wpcf7mailfailed", "wpcf7spam"].forEach((evt) => {
    wrapper.addEventListener(evt, () => {
      if (confirmSubmit) {
        confirmSubmit.disabled = false;
      }

      setMode("input");
      scrollToResponse(wrapper);
    });
  });

  wrapper.addEventListener(
    "wpcf7mailsent",
    () => {
      goToThanks(form);
    }
    // (setMode("complete"), form.reset()),
  );
  wrapper.addEventListener("wpcf7mailfailed", () => {
    if (isLocal) {
      goToThanks(form);
      return;
    }

    setMode("input");
    scrollToResponse(wrapper);
  });
  completeClose?.addEventListener(
    "click",
    (e) => (e.preventDefault(), setMode("input")),
  );
};

const init = () => document.querySelectorAll(".wpcf7 form").forEach(setupForm);

document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", init)
  : init();

const goToThanks = (form) => {
  // 送信元ページのスラッグ
  const path = window.location.pathname.replace(/\/$/, "");
  const formSlug = decodeURIComponent(path.split("/").pop() || "");

  // 学科名 → パラメータ用slug
  const courseMap = {
    "理学療法学科": "physiotherapist",
    "理学療法": "physiotherapist",
    "東洋医療総合学科": "oriental_medical",
    "東洋医療総合" : "oriental_medical",
    "看護": "nurse",
    "看護学科": "nurse",
    "看護学科二年課程（通信制）": "nurse-correspondence",
    "看護 二年過程": "nurse-correspondence",
    "看護学科二年課程": "nurse-correspondence",
  };

  // Contact Form 7 の course
  const courseField = form.querySelector('[name="course"]');
  const courseName = courseField ? courseField.value : "";
  const courseValue = courseMap[courseName] || "";

  const params = new URLSearchParams();

  if (formSlug) {
    params.set("source_form", formSlug);
  }

  if (courseValue) {
    params.set("selected_course", courseValue);
  }

  window.location.href = `/thanks/?${params.toString()}`;
};